import React, { useCallback, useEffect, useRef, useState } from 'react';

import apiBaseUrl from '../../utils/config.js';
import { authFetch } from '../../utils/authFetch.js';
import clinicalNoteService from './clinicalNoteService.js';
import ClinicalNotePainSection from './ClinicalNotePainSection.jsx';
import ClinicalNoteRomSection from './ClinicalNoteRomSection.jsx';
import ClinicalNoteStrengthSection from './ClinicalNoteStrengthSection.jsx';
import ClinicalNoteGoalSection from './ClinicalNoteGoalSection.jsx';
import ClinicalNoteContentSection from './ClinicalNoteContentSection.jsx';
import ClinicalNoteTreatmentSection from './ClinicalNoteTreatmentSection.jsx';
import ClinicalNoteCollapsibleSection from './ClinicalNoteCollapsibleSection.jsx';
import useClinicalSectionStatuses from './useClinicalSectionStatuses.js';

const AUTOSAVE_DELAY_MS = 900;
const MAX_REASON_LENGTH = 3000;

/**
 * Saves the Daily-only explanation using the existing cross-section mutation
 * queue. The queue fetches the latest expectedVersion immediately before PATCH.
 * The backend remains authoritative about whether an explanation is required.
 */
async function saveDailyNoChangeReason(clinicalNoteId, reason) {
  return clinicalNoteService.runSerializedSectionSave(
    clinicalNoteId,
    async (expectedVersion) => {
      const response = await authFetch(
        `${apiBaseUrl}/clinical-notes/${encodeURIComponent(clinicalNoteId)}/daily-no-change-reason`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ expectedVersion, reason }),
        }
      );
      const body = await response.text();
      let data = null;
      if (body) {
        try { data = JSON.parse(body); } catch { data = { message: body }; }
      }
      if (!response.ok) {
        throw new Error(data?.message || data?.error ||
          `Could not save the Daily Note explanation (HTTP ${response.status}).`);
      }
      return data;
    }
  );
}

/**
 * Draft-only editor. Remains mounted when the containing accordion collapses,
 * so pending edits participate in visit-navigation safety checks.
 */
function DailyNoChangeReasonSection({
  clinicalNoteId,
  disabled,
  onSaved,
  onVersionChange,
  navigationCoordinator,
}) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirtyTick, setDirtyTick] = useState(0);

  const mountedRef = useRef(false);
  const loadedRef = useRef(false);
  const loadErrorRef = useRef('');
  const draftRef = useRef('');
  const savedRef = useRef('');
  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const savePromiseRef = useRef(null);
  const flushRef = useRef(null);
  const saveErrorRef = useRef('');
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    loadedRef.current = false;
    loadErrorRef.current = '';
    saveErrorRef.current = '';
    revisionRef.current = 0;
    savedRevisionRef.current = 0;
    draftRef.current = '';
    savedRef.current = '';
    setValue('');
    setLoading(true);
    setLoadError('');
    setSaveError('');

    (async () => {
      try {
        const note = await clinicalNoteService.getClinicalNote(clinicalNoteId);
        if (cancelled) return;
        if (note?.noteType !== 'DAILY') {
          throw new Error('The no-change explanation is only available for Daily Notes.');
        }
        const original = note?.dailyNoChangeReason ?? '';
        draftRef.current = original;
        savedRef.current = original;
        setValue(original);
        loadedRef.current = true;
      } catch (error) {
        if (cancelled) return;
        loadErrorRef.current = error?.message || 'Unable to load the Daily Note explanation.';
        setLoadError(loadErrorRef.current);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [clinicalNoteId]);

  const flush = useCallback(() => {
    if (savePromiseRef.current) return savePromiseRef.current;
    if (!mountedRef.current || !loadedRef.current || disabledRef.current ||
        revisionRef.current === savedRevisionRef.current) return Promise.resolve();

    const revision = revisionRef.current;
    const reason = draftRef.current;
    setSaving(true);
    setSaveError('');
    saveErrorRef.current = '';

    const promise = saveDailyNoChangeReason(clinicalNoteId, reason)
      .then(async (response) => {
        if (!mountedRef.current) return;
        savedRef.current = reason;
        savedRevisionRef.current = revision;
        if (typeof onVersionChange === 'function' && response?.entityVersion != null) {
          onVersionChange(response.entityVersion, response);
        }
        if (typeof onSaved === 'function') await onSaved(response);
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        saveErrorRef.current = error?.message || 'The explanation could not be saved.';
        setSaveError(saveErrorRef.current);
      })
      .finally(() => {
        if (savePromiseRef.current === promise) savePromiseRef.current = null;
        if (mountedRef.current) setSaving(false);
      });
    savePromiseRef.current = promise;
    return promise;
  }, [clinicalNoteId, onSaved, onVersionChange]);

  flushRef.current = flush;

  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'daily-no-change-reason', {
      flush: async () => {
        if (savePromiseRef.current) await savePromiseRef.current;
        if (loadedRef.current && !disabledRef.current &&
            revisionRef.current !== savedRevisionRef.current) {
          await flushRef.current?.();
        }
        if (savePromiseRef.current) await savePromiseRef.current;
      },
      getState: () => ({
        dirty: !loadedRef.current || revisionRef.current !== savedRevisionRef.current,
        saving: Boolean(savePromiseRef.current),
        invalid: draftRef.current.length > MAX_REASON_LENGTH,
        error: loadErrorRef.current || saveErrorRef.current ||
          (!loadedRef.current ? 'The Daily Note explanation is still loading.' : ''),
      }),
    });
  }, [clinicalNoteId, navigationCoordinator]);

  useEffect(() => {
    if (!loadedRef.current || disabled ||
        revisionRef.current === savedRevisionRef.current || saveError) return undefined;
    const timer = window.setTimeout(() => { void flushRef.current?.(); }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [dirtyTick, disabled, saveError]);

  const onChange = (nextValue) => {
    if (disabled || nextValue.length > MAX_REASON_LENGTH) return;
    draftRef.current = nextValue;
    revisionRef.current += 1;
    saveErrorRef.current = '';
    setSaveError('');
    setValue(nextValue);
    setDirtyTick((n) => n + 1);
  };

  return (
    <section style={{ padding: 14 }}>
      <p style={{ marginTop: 0, fontSize: 13 }}>
        If Pain, ROM, and Strength have not changed from the previous visit,
        document the clinical reason here. This explanation is optional while
        drafting; final validation determines whether it is required.
      </p>
      {loading ? (
        <p>Loading explanation…</p>
      ) : loadError ? (
        <div role="alert" style={{ color: '#b42318' }}>{loadError}</div>
      ) : (
        <>
          <label htmlFor={`daily-no-change-reason-${clinicalNoteId}`}
            style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>
            Reason for no documented clinical change
          </label>
          <textarea
            id={`daily-no-change-reason-${clinicalNoteId}`}
            rows={4}
            disabled={disabled}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            maxLength={MAX_REASON_LENGTH}
            placeholder="Explain why clinical measurements remain unchanged, when applicable…"
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical',
              borderRadius: 7, border: '1px solid #b8bec7', padding: 10,
              fontSize: 14, background: disabled ? '#f3f4f6' : '#fff' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between',
            gap: 10, flexWrap: 'wrap', marginTop: 6, fontSize: 12 }}>
            <span aria-live="polite" style={{ color: saveError ? '#b42318' : '#58616c' }}>
              {saveError ? 'Autosave failed' : saving ? 'Saving…' :
                revisionRef.current !== savedRevisionRef.current ? 'Unsaved changes' :
                disabled ? 'Read only' : 'Saved'}
            </span>
            <span>{value.length}/{MAX_REASON_LENGTH}</span>
          </div>
          {saveError && (
            <div role="alert" style={{ marginTop: 8, color: '#b42318' }}>
              {saveError}{' '}
              {!disabled && (
                <button type="button" onClick={() => {
                  saveErrorRef.current = '';
                  setSaveError('');
                  void flushRef.current?.();
                }}>Retry Save</button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Rendering and accordion navigation follow one shared order. */
const DAILY_SECTION_KEYS = [
  'pain', 'rom', 'strength', 'goals', 'content', 'treatment', 'no-change',
];

/**
 * Compact Daily clinical-note form. All sections remain mounted when collapsed.
 * The backend, not the accordion, decides whether the explanation is required.
 */
export default function ClinicalNoteDailyForm({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
}) {
  const resetKey = String(clinicalNoteId ?? 'new');
  const accordionGroup = `clinical-note-${resetKey}`;
  const { statuses, refreshSectionStatuses } =
    useClinicalSectionStatuses(clinicalNoteId);

  const handleSaved = useCallback(async (...args) => {
    try {
      if (typeof onSaved === 'function') await onSaved(...args);
    } finally {
      await refreshSectionStatuses();
    }
  }, [onSaved, refreshSectionStatuses]);

  const commonProps = {
    clinicalNoteId, disabled, onSaved: handleSaved,
    onVersionChange, navigationCoordinator,
  };

  const accordionProps = (key, index) => ({
    sectionKey: `${resetKey}-${key}`,
    accordionGroup,
    previousSectionKey: index > 0 ? `${resetKey}-${DAILY_SECTION_KEYS[index - 1]}` : null,
    nextSectionKey: index < DAILY_SECTION_KEYS.length - 1
      ? `${resetKey}-${DAILY_SECTION_KEYS[index + 1]}` : null,
  });

  return (
    <div className="clinical-note-section-stack">
      <ClinicalNoteCollapsibleSection
        title="Pain" defaultCollapsed={false} status={statuses.PAIN}
        resetKey={`${resetKey}-pain`} {...accordionProps('pain', 0)}>
        <ClinicalNotePainSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
      <ClinicalNoteCollapsibleSection
        title="Range of Motion" defaultCollapsed status={statuses.ROM}
        resetKey={`${resetKey}-rom`} {...accordionProps('rom', 1)}>
        <ClinicalNoteRomSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
      <ClinicalNoteCollapsibleSection
        title="Strength / GMMT" defaultCollapsed status={statuses.STRENGTH}
        resetKey={`${resetKey}-strength`} {...accordionProps('strength', 2)}>
        <ClinicalNoteStrengthSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
      <ClinicalNoteCollapsibleSection
        title="Goals" defaultCollapsed status={statuses.GOAL}
        resetKey={`${resetKey}-goals`} {...accordionProps('goals', 3)}>
        <ClinicalNoteGoalSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
      <ClinicalNoteCollapsibleSection
        title="Clinical Content / Narrative" defaultCollapsed status={statuses.CONTENT}
        resetKey={`${resetKey}-content`} {...accordionProps('content', 4)}>
        <ClinicalNoteContentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
      <ClinicalNoteCollapsibleSection
        title="Treatment" defaultCollapsed status={statuses.TREATMENT}
        resetKey={`${resetKey}-treatment`} {...accordionProps('treatment', 5)}>
        <ClinicalNoteTreatmentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
      <ClinicalNoteCollapsibleSection
        title="No Clinical Change — Explanation" defaultCollapsed
        status={undefined} resetKey={`${resetKey}-no-change`}
        {...accordionProps('no-change', 6)}>
        <DailyNoChangeReasonSection key={resetKey} {...commonProps} />
      </ClinicalNoteCollapsibleSection>
    </div>
  );
}
